/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-01-20 08:54:07
 *
 *******************************************************/
// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  GET_MERCHANDISING_REQUEST,
  GET_MERCHANDISING_SUCCESS,
  GET_MERCHANDISING_FAILURE,
  UPDATE_MERCHANDISING_RULE_REQUEST,
  UPDATE_MERCHANDISING_RULE_SUCCESS,
  UPDATE_MERCHANDISING_RULE_FAILURE,
  DELETE_MERCHANDISING_RULE_REQUEST,
  DELETE_MERCHANDISING_RULE_SUCCESS,
  DELETE_MERCHANDISING_RULE_FAILURE,
  REORDER_MERCHANDISING_RULES_REQUEST,
  REORDER_MERCHANDISING_RULES_SUCCESS,
  REORDER_MERCHANDISING_RULES_FAILURE,
  GET_MERCHANDISING_RULE_NAMES_REQUEST,
  GET_MERCHANDISING_RULE_NAMES_SUCCESS,
  GET_MERCHANDISING_RULE_NAMES_FAILURE,
  CREATE_MERCHANDISING_REQUEST,
  CREATE_MERCHANDISING_SUCCESS,
  CREATE_MERCHANDISING_FAILURE,
  GET_MERCHANDISING_RULE_REQUEST,
  GET_MERCHANDISING_RULE_SUCCESS,
  GET_MERCHANDISING_RULE_FAILURE,
  SHOW_FEEDBACK_VERSION,
  HIDE_FEEDBACK_VERSION,
  SEND_FEEDBACK_MERCHANDISING_VERSION_REQUEST,
  SEND_FEEDBACK_MERCHANDISING_VERSION_SUCCESS,
  SEND_FEEDBACK_MERCHANDISING_VERSION_FAILURE
} from 'stores/flow-type/merchandising.type';

/**
 * call to get merchandisings
 * @return {object} The RSAA object for API middleware
 */
export const getMerchandisings = (q: string, skip: number, limit: number, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.merchandising.get-rules',
    { queries: { q, skip, limit } },
    [
      GET_MERCHANDISING_REQUEST,
      {
        type: GET_MERCHANDISING_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_MERCHANDISING_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Merchandising.Actions.getMerchandisings'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to update merchandising
 * @return {object} The RSAA object for API middleware
 */
export const updateMerchandising = (
  ruleId: string,
  merchandising: Object,
  actionName: string = '',
  i18n: I18n
) => {
  const params = { ruleId };
  return apiService.sendRequest(
    'admin-v3.merchandising.update-rule',
    { params, body: merchandising },
    [
      UPDATE_MERCHANDISING_RULE_REQUEST,
      {
        type: UPDATE_MERCHANDISING_RULE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'custom-with-sync',
            actionName === 'changeStatus'
              ? i18n.translate(
                  'AdminHTML.Merchandising.Actions.updateMerchandising_Success_ChangeStatus'
                )
              : i18n.translate(
                  'AdminHTML.Merchandising.Actions.updateMerchandising_Success_NotChangeStatus'
                ),
            i18n
          );

          return { data, actionName, merchandising };
        }
      },
      {
        type: UPDATE_MERCHANDISING_RULE_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            actionName === 'changeStatus'
              ? i18n.translate(
                  'AdminHTML.Merchandising.Actions.updateMerchandising_Failure_ChangeStatus'
                )
              : i18n.translate(
                  'AdminHTML.Merchandising.Actions.updateMerchandising_Failure_NotChangeStatus'
                ),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to delete merchandising
 * @return {object} The RSAA object for API middleware
 */
export const deleteMerchandising = (ruleId: string, i18n: I18n) => {
  const params = { ruleId };
  return apiService.sendRequest('admin-v3.merchandising.delete-rule', { params }, [
    DELETE_MERCHANDISING_RULE_REQUEST,
    {
      type: DELETE_MERCHANDISING_RULE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom-with-sync',
          i18n.translate('AdminHTML.Merchandising.Actions.deleteMerchandising_Success'),
          i18n
        );

        return { data };
      }
    },
    {
      type: DELETE_MERCHANDISING_RULE_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Merchandising.Actions.deleteMerchandising_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to reorder merchandising
 * @return {object} The RSAA object for API middleware
 */
export const reorderMerchandisings = (newOrders: number, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.merchandising.reorder-rules', { body: newOrders }, [
    REORDER_MERCHANDISING_RULES_REQUEST,
    {
      type: REORDER_MERCHANDISING_RULES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom-with-sync',
          i18n.translate('AdminHTML.Merchandising.Actions.reorderMerchandisings_Success'),
          i18n
        );

        return { data };
      }
    },
    {
      type: REORDER_MERCHANDISING_RULES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Merchandising.Actions.reorderMerchandisings_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get merchandising rule name
 * @return {object} The RSAA object for API middleware
 */
export const getMerchandisingRuleNames = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.merchandising.rule-names', {}, [
    GET_MERCHANDISING_RULE_NAMES_REQUEST,
    {
      type: GET_MERCHANDISING_RULE_NAMES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_MERCHANDISING_RULE_NAMES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Merchandising.Actions.getMerchandisingRuleNames'),
          i18n
        )
    }
  ]);
};

/**
 * call to create merchandising rule
 * @return {object} The RSAA object for API middleware
 */
export const createMerchandising = (merchandising: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.merchandising.create-rule', { body: merchandising }, [
    CREATE_MERCHANDISING_REQUEST,
    {
      type: CREATE_MERCHANDISING_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom-with-sync',
          i18n.translate('AdminHTML.Merchandising.Actions.createMerchandising_Success'),
          i18n
        );

        return { data, merchandising };
      }
    },
    {
      type: CREATE_MERCHANDISING_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Merchandising.Actions.createMerchandising_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to get merchandising rule
 * @return {object} The RSAA object for API middleware
 */
export const getMerchandising = (ruleId: string, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.merchandising.get-rule', { params: { ruleId } }, [
    GET_MERCHANDISING_RULE_REQUEST,
    {
      type: GET_MERCHANDISING_RULE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_MERCHANDISING_RULE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Merchandising.Actions.getMerchandising'),
          i18n
        )
    }
  ]);
};

/**
 * call to show feedback version when switching from v2
 * @return {object} The RSAA object for API middleware
 */
export const showFeedbackVersion = (i18n: I18n) => {
  return { type: SHOW_FEEDBACK_VERSION, payload: {} };
};

/**
 * call to hide feedback version when switching from v2
 * @return {object} The RSAA object for API middleware
 */
export const hideFeedbackVersion = (i18n: I18n) => {
  return { type: HIDE_FEEDBACK_VERSION, payload: {} };
};

/**
 * call to send feedback version when switching from v2
 * @return {object} The RSAA object for API middleware
 */
export const sendFeedback = (body: Object, meta, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.merchandising.send-version-feedback', { body }, [
    { type: SEND_FEEDBACK_MERCHANDISING_VERSION_REQUEST },
    {
      type: SEND_FEEDBACK_MERCHANDISING_VERSION_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'success',
          i18n.translate('AdminHTML.Merchandising.Actions.sendFeedback_Success'),
          i18n
        );
        return { ...data };
      },
      meta
    },
    {
      type: SEND_FEEDBACK_MERCHANDISING_VERSION_FAILURE,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Merchandising.Actions.sendFeedback_Failure'),
          i18n
        );
        return { ...data };
      },
      meta
    }
  ]);
};
